import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as Sentry from '@sentry/browser';
import { isUndefined, pickBy, unionBy } from 'lodash';
import qs from 'querystringify';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { useIsNewInvoicingFlow } from 'src/components/NewInvoicing/hooks/useIsNewInvoicingFlow';

import { memberClient } from '../../config/Apollo';
import usePrevious from '../../hooks/usePrevious';
import { BulkMessageFailureModal } from '../../components/Clients/BulkMessageFailureModal';
import { ActiveFilter, BulkMessageModal } from '../../components/Clients/BulkMessageModal';
import { BulkMessagePreviewModal } from '../../components/Clients/BulkMessagePreviewModal';
import { BulkMessageSuccessModal } from '../../components/Clients/BulkMessageSuccessModal';
import { ClientsTable } from '../../components/Clients/ClientsTable';
import {
  ALL_CLIENT_FILTERS,
  ClientFilter,
  EmptyMatchStatusFilterSelection,
  FilterSection,
  InitialMatchStatusFilterSelection,
} from '../../components/Clients/FilterSection';
import { PaginationRow, PaginationSize } from '../../components/Clients';
import ReportVisitDialog from '../../components/Dialogs/ReportVisit';
import MemberProfileDrawer from '../../components/MemberProfileDrawer/MemberProfileDrawer';
import PageContainer from '../../components/PageContainer';
import SearchInput from '../../components/SearchInput';
import { findConversationUuid } from '../../helpers/findConversationUuid';
import { buildWho5Query, getVariablesFromPageChange } from './helpers';
import {
  ClientsSearchFieldTestID,
  LOCATION_LOCAL_STORAGE_KEY,
  MatchParams,
  MAXIMUM_BULK_MESSAGE_COUNT,
  PageInfo,
  QueryParams,
} from './constants';
import { getCurrentUser, getMyAssignmentWithMember } from '../../selectors/userSelectors';
import { ClientSummaryNode } from '../../generated/webapp_gql';
import { ClientsPageQuery, useClientsPageLazyQuery } from '../documents/ClientsPageQuery.generated';

const useStyles = makeStyles({
  loadingContainer: {
    alignSelf: 'center',
    paddingTop: 40,
  },
});

type Props = RouteComponentProps<MatchParams>;

const ClientsPage: React.FC<Props> = ({ match, history, location }) => {
  const previousLocationSearch = localStorage.getItem(LOCATION_LOCAL_STORAGE_KEY);

  const defaultLocation: string =
    previousLocationSearch !== null ? previousLocationSearch : location.search;

  const queryParams: QueryParams = qs.parse(defaultLocation);

  const user = useSelector(getCurrentUser);

  const [reportVisitModalOpen, setReportVisitModalOpen] = useState(false);
  const [memberProfileDrawerOpen, setMemberProfileDrawerOpen] = useState(!!match.params.memberUuid);
  const [currentMemberUuid, setCurrentMemberUuid] = useState(match.params.memberUuid);

  const currentAssignment = useSelector(getMyAssignmentWithMember(currentMemberUuid));

  // pagination
  const [pageSize, setPageSize] = useState<number>(
    queryParams.pageSize ? parseInt(queryParams.pageSize, PaginationSize.Ten) : 10,
  );
  const previousPageSize = usePrevious(pageSize);

  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);

  const [pageNumber, setPageNumber] = useState<number>(1);

  const previousPageNumber = usePrevious(pageNumber);

  const [currentPageCursors, setCurrentPageCursors] = useState<PageInfo>({
    startCursor: '',
    endCursor: '',
  });

  const showCareForm = useRef((location.search || '').includes('care-form=true'));

  const defaultMatchedFilter = InitialMatchStatusFilterSelection.join(',');
  const [matchedFilter, setMatchedFilter] = useState(
    queryParams.matchedFilter || defaultMatchedFilter,
  );
  const [searchFilter, setSearchFilter] = useState(queryParams.searchFilter || '');

  // total number of clients for the current filter
  const [memberCountFiltered, setMemberCountFiltered] = useState(0);

  const [filteredMembers, setFilteredMembers] = useState<Array<ClientSummaryNode>>([]);

  const [orderBy, setOrderBy] = useState(queryParams.orderBy || '');

  const [hasUpcomingBooking, setHasUpcomingBooking] = useState(
    queryParams.hasUpcomingBooking || '',
  );
  const [hasRemainingSessions, setHasRemainingSessions] = useState(
    queryParams.hasRemainingSessions || '',
  );
  const [hasAppInstalled, setHasAppInstalled] = useState(queryParams.hasAppInstalled || '');

  const [companiesFilter, setCompaniesFilter] = useState(queryParams.companiesFilter || '');

  const [who5Filter, setWho5Filter] = useState(queryParams.who5Filter || '');

  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [showBulkMessagePreviewModal, setShowBulkMessagePreviewModal] = useState(false);
  const [showBulkMessageSuccessModal, setShowBulkMessageSuccessModal] = useState(false);
  const [showBulkMessageFailureModal, setShowBulkMessageFailureModal] = useState(false);

  const [bulkMessageText, setBulkMessageText] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<ClientSummaryNode[]>([]);
  const [membersToBulkMessage, setMembersToBulkMessage] = useState<ClientSummaryNode[]>([]);
  const [hasSelectedAllClientsOnPage, setHasSelectedAllClientsOnPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const handleClientFetch = useCallback(
    (data: ClientsPageQuery | undefined) => {
      if (data) {
        setMemberCountFiltered(data.clients?.totalCount || 0);

        if (data.clients?.pageInfo) {
          const { endCursor, startCursor } = data.clients.pageInfo;

          setCurrentPageCursors({ endCursor: endCursor || '', startCursor: startCursor || '' });
        }
      }
    },
    [setCurrentPageCursors, setMemberCountFiltered],
  );

  const upcomingBookingQuery =
    hasUpcomingBooking !== '' ? { hasUpcomingBooking: hasUpcomingBooking === 'true' } : {};

  const hasRemainingSessionsQuery =
    hasRemainingSessions !== '' ? { hasRemainingSessions: hasRemainingSessions === 'true' } : {};

  const hasAppInstalledQuery =
    hasAppInstalled !== '' ? { appInstalled: hasAppInstalled === 'true' } : {};

  const [fetchClients, { data, error, fetchMore, loading }] = useClientsPageLazyQuery({
    client: memberClient,
    fetchPolicy: 'network-only',
    onCompleted: handleClientFetch,
    variables: {
      companies: companiesFilter,
      first: pageSize,
      matchStatus: matchedFilter || EmptyMatchStatusFilterSelection.join(','),
      orderBy,
      search: searchFilter,
      ...upcomingBookingQuery,
      ...hasRemainingSessionsQuery,
      ...buildWho5Query(who5Filter),
      ...hasAppInstalledQuery,
    },
  });

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (loading) return;

    if (error) {
      Sentry.captureException(error);
      data?.clients?.edges?.length === 0 && setFilteredMembers([]);
      return;
    }

    const clients =
      data?.clients?.edges?.reduce((previousValue, currentValue) => {
        if (currentValue?.node) {
          previousValue.push(currentValue.node);
        }

        return previousValue;
      }, [] as ClientSummaryNode[]) ?? [];

    setFilteredMembers(clients);
  }, [data, error, loading]);

  const onSelectMember = (selectedMember: ClientSummaryNode) => {
    const hasMember = selectedMembers.find(
      (member) => member.memberUuid === selectedMember.memberUuid,
    );

    if (hasMember) {
      const nextMembers = selectedMembers.filter(
        (member) => member.memberUuid !== selectedMember.memberUuid,
      );
      setSelectedMembers(nextMembers);
      return;
    }

    if (selectedMembers.length === MAXIMUM_BULK_MESSAGE_COUNT) {
      return;
    }
    const nextMembers = selectedMembers.slice();
    nextMembers.push(selectedMember);
    setSelectedMembers(nextMembers);
  };

  const onSelectAllClientsOnPage = () => {
    if (!hasSelectedAllClientsOnPage && selectedMembers.length < MAXIMUM_BULK_MESSAGE_COUNT) {
      const nextSelectedMembers = unionBy(selectedMembers, filteredMembers, 'memberUuid').slice(
        0,
        MAXIMUM_BULK_MESSAGE_COUNT,
      );
      setSelectedMembers(nextSelectedMembers);
      setHasSelectedAllClientsOnPage(true);

      return;
    }

    const nextMembers = selectedMembers.filter((member) => {
      return !filteredMembers.find(
        (filteredMember) => filteredMember.memberUuid === member.memberUuid,
      );
    });

    setHasSelectedAllClientsOnPage(false);
    setSelectedMembers(nextMembers);
  };

  const handleClearAllSelectedMembers = () => {
    setHasSelectedAllClientsOnPage(false);
    setSelectedMembers([]);
  };

  const updateQueryParams = useCallback(
    (path: string) => {
      const params = pickBy(
        {
          companiesFilter,
          hasAppInstalled,
          hasRemainingSessions,
          hasUpcomingBooking,
          matchedFilter,
          orderBy,
          pageSize,
          searchFilter,
          who5Filter,
        },
        (value) => !(value === '' || value === 0),
      );

      history.push({
        pathname: path,
        search: qs.stringify(params),
      });
    },
    [
      companiesFilter,
      hasAppInstalled,
      hasRemainingSessions,
      hasUpcomingBooking,
      history,
      matchedFilter,
      orderBy,
      pageSize,
      searchFilter,
      who5Filter,
    ],
  );

  const closeMemberProfileDrawer = () => {
    updateQueryParams('/clients');
    setCurrentMemberUuid(undefined);
    setMemberProfileDrawerOpen(false);
  };

  const handleClientsPageQueryUpdate = useCallback(
    (previous, { fetchMoreResult }: { fetchMoreResult?: ClientsPageQuery }) => {
      updateQueryParams(window.location.pathname);

      if (!fetchMoreResult) return previous;

      handleClientFetch(fetchMoreResult);

      return fetchMoreResult;
    },
    [handleClientFetch, updateQueryParams],
  );

  const previousLoadingState = usePrevious(loading);

  const lastPage = Math.floor(memberCountFiltered / pageSize) + 1;

  useEffect(() => {
    if (
      isUndefined(previousPageNumber) ||
      (pageNumber === previousPageNumber && pageSize === previousPageSize)
    )
      return;

    const variables = getVariablesFromPageChange({
      currentPageCursors,
      lastPage,
      pageNumber,
      previousPageNumber,
      pageSize,
    });

    fetchMore({
      variables,
      updateQuery: handleClientsPageQueryUpdate,
    });
  }, [
    currentPageCursors,
    fetchMore,
    handleClientsPageQueryUpdate,
    lastPage,
    pageNumber,
    pageSize,
    previousPageNumber,
    previousPageSize,
  ]);

  useEffect(() => {
    if (loading && previousLoadingState !== loading) {
      updateQueryParams(window.location.pathname);
    }
  }, [loading, previousLoadingState, updateQueryParams]);

  useEffect(() => {
    if (error && !loading) {
      localStorage.removeItem(LOCATION_LOCAL_STORAGE_KEY);
    }
  }, [error, loading]);

  useEffect(() => {
    localStorage.setItem(LOCATION_LOCAL_STORAGE_KEY, location.search);
  }, [location.search]);

  useEffect(() => {
    function getActiveFilters(): ActiveFilter[] {
      const currentActiveFilters: ActiveFilter[] = [];
      ALL_CLIENT_FILTERS.forEach((filterKey: ClientFilter) => {
        switch (filterKey) {
          case ClientFilter.Matched:
            if (matchedFilter) {
              const matchStatuses = matchedFilter.split(',');
              currentActiveFilters.push({
                filterKey: ClientFilter.Matched,
                displayValue:
                  matchStatuses.length === 1
                    ? matchedFilter
                    : `Match Status (${matchStatuses.length})`,
              });
            }
            break;
          case ClientFilter.HasUpcomingBooking:
            if (hasUpcomingBooking) {
              currentActiveFilters.push({
                filterKey: ClientFilter.HasUpcomingBooking,
                displayValue: hasUpcomingBooking === 'true' ? 'Session Scheduled' : 'Not Scheduled',
              });
            }
            break;
          case ClientFilter.HasRemainingSessions:
            if (hasRemainingSessions) {
              currentActiveFilters.push({
                filterKey: ClientFilter.HasRemainingSessions,
                displayValue:
                  hasRemainingSessions === 'true' ? 'Sessions Available' : 'No Sessions Left',
              });
            }
            break;
          case ClientFilter.Who5:
            if (who5Filter) {
              const who5Selections = who5Filter.split(',');
              currentActiveFilters.push({
                filterKey: ClientFilter.Who5,
                displayValue:
                  who5Selections.length === 1 ? who5Filter : `WHO-5 (${who5Selections.length})`,
              });
            }
            break;
          case ClientFilter.HasAppInstalled:
            if (hasAppInstalled) {
              currentActiveFilters.push({
                filterKey: ClientFilter.HasAppInstalled,
                displayValue: hasAppInstalled === 'true' ? 'App Installed' : 'App Not Installed',
              });
            }
            break;
          case ClientFilter.Companies:
            const companies = companiesFilter.split(',');
            if (companiesFilter && filteredMembers.length > 0) {
              // The selected company name is not bubbled up to the clients page,
              // so for displaying a single company, pull the name from the members data.
              let companyDisplayValue = '';
              if (companies.length === 1) {
                companyDisplayValue = (filteredMembers[0] as ClientSummaryNode).companyName;
              } else {
                companyDisplayValue = `Companies (${companies.length})`;
              }
              currentActiveFilters.push({
                filterKey: ClientFilter.Companies,
                displayValue: companyDisplayValue,
              });
            }
            break;
          default:
            const exhaustiveCheck: never = filterKey;
            Sentry.captureMessage(`Unknown Clients Page filter: ${exhaustiveCheck}`);
        }
      });
      return currentActiveFilters;
    }
    setActiveFilters(getActiveFilters());
  }, [
    matchedFilter,
    hasUpcomingBooking,
    hasRemainingSessions,
    who5Filter,
    hasAppInstalled,
    companiesFilter,
    filteredMembers,
  ]);

  const handlePaginationRowSetPageNumber = (newPageNumber: number) => {
    setPageNumber(newPageNumber);
  };

  const handlePaginationRowSetPageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const handleReportVisitSuccess = () => {
    setReportVisitModalOpen(false);
  };

  const handleClearAllFilters = () => {
    setMatchedFilter('');
    setHasUpcomingBooking('');
    setHasRemainingSessions('');
    setWho5Filter('');
    setHasAppInstalled('');
    setOrderBy('');
    setCompaniesFilter('');
    setPageNumber(1);
  };

  const handleShowBulkMessageModal = () => {
    setShowBulkMessageModal(true);

    // TODO: Firebase Analytics Removal
    // logEvent('clicked_bulk_message', {
    //   numberOfSelectedMembers: selectedMembers.length,
    //   activeFilters,
    // });
  };

  const handleSelectRow = (memberUuid) => {
    if (currentMemberUuid === memberUuid) {
      closeMemberProfileDrawer();
    } else {
      updateQueryParams(`/clients/${memberUuid}`);
      setCurrentMemberUuid(memberUuid);
      setMemberProfileDrawerOpen(true);
    }
  };

  const handleSelectChat = async (e, memberUuid) => {
    e.stopPropagation();

    const mostRecentMemberConversation = await findConversationUuid(memberUuid, user.uuid);

    history.push(`/chat/${mostRecentMemberConversation || ''}`);
  };

  const isNewInvoicingFlow = useIsNewInvoicingFlow();

  const handleReportVisit = (e, memberUuid) => {
    e.stopPropagation();
    if (isNewInvoicingFlow) {
      history.push(`/invoicing?memberUuid=${memberUuid}`);
    } else {
      setCurrentMemberUuid(memberUuid);
      setReportVisitModalOpen(true);
    }
  };

  const handleReportVisitCancel = () => setReportVisitModalOpen(false);

  const handleBulkMessageModalClose = () => {
    setShowBulkMessageModal(false);
    setBulkMessageText('');
  };

  const handleBulkMessageModalPreview = () => {
    setShowBulkMessagePreviewModal(true);
    setShowBulkMessageModal(false);
  };

  const handleBulkMessagePreviewModalClose = () => {
    setShowBulkMessagePreviewModal(false);
    setBulkMessageText('');
  };

  const handleBulkMessagePreviewBackToEdit = () => {
    setShowBulkMessagePreviewModal(false);
    setShowBulkMessageModal(true);
  };

  const handleBulkMessagePreviewSendSuccess = (startTime, endTime) => {
    setBulkMessageText('');
    setHasSelectedAllClientsOnPage(false);
    setSelectedMembers([]);
    setMembersToBulkMessage([]);
    setShowBulkMessagePreviewModal(false);
    setShowBulkMessageSuccessModal(true);

    // TODO: Firebase Analytics Removal
    // logEvent('bulk_messages_sent', {
    //   sendTime: endTime - startTime,
    // });
  };

  const handleBulkMessagePreviewSendFailure = (startTime, endTime) => {
    setShowBulkMessagePreviewModal(false);
    setShowBulkMessageFailureModal(true);

    // TODO: Firebase Analytics Removal
    // logEvent('bulk_messages_sent', {
    //   sendTime: endTime - startTime,
    // });
  };

  const handleBulkMessageSuccessClose = () => setShowBulkMessageSuccessModal(false);

  const handleBulkMessageFailureClose = () => setShowBulkMessageFailureModal(false);

  const handleBulkMessageFailureBackToEdit = () => {
    setShowBulkMessageFailureModal(false);
    setShowBulkMessageModal(true);
  };

  const handleBulkMessageFailureRetry = () => {
    setShowBulkMessageFailureModal(false);
    setShowBulkMessagePreviewModal(true);
  };

  const fetchClientsOnFilterChange = () => {
    setPageNumber(1);
    fetchClients();
  };

  const handleChatAvailableFilterChange = (filter) => {
    setHasAppInstalled(filter);
    fetchClientsOnFilterChange();
  };

  const handleCompanyFilterChange = (filter) => {
    setCompaniesFilter(filter);
    fetchClientsOnFilterChange();
  };

  const handleSessionRemainingFilterChange = (filter) => {
    setHasRemainingSessions(filter);
    fetchClientsOnFilterChange();
  };

  const handleWho5FilterChange = (filter) => {
    setWho5Filter(filter);
    fetchClientsOnFilterChange();
  };

  const handleMatchStatusFilterChange = (filter) => {
    setMatchedFilter(filter);
    fetchClientsOnFilterChange();
  };

  const handleHasUpcomingBookingFilterChange = (filter) => {
    setHasUpcomingBooking(filter);
    fetchClientsOnFilterChange();
  };

  const handleSearchInputChange = (e) => {
    setSearchFilter(e.target.value);
    fetchClientsOnFilterChange();
  };

  const classes = useStyles();

  if (!loading && error && filteredMembers.length === 0) {
    return (
      <PageContainer>
        <div className="flex flex-col w-full p-8 bg-white overflow-y">
          Couldn't retrieve member information
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer position="inherit">
      <div className="flex flex-col w-full px-8 pt-8 pb-20 overflow-y-auto">
        <div className="flex flex-row justify-between">
          <h1 className="text-2xl font-extrabold">Client Management</h1>
          <div className="w-1/2 md:w-2/5 xl:w-1/4">
            <SearchInput
              dataTestID={ClientsSearchFieldTestID}
              inputClassName="bg-white border border-gray-300 w-full placeholder-gray-800"
              onChange={handleSearchInputChange}
              placeholder="Search for clients..."
              value={searchFilter}
            />
          </div>
        </div>

        <FilterSection
          companiesFilter={companiesFilter}
          hasAppInstalled={hasAppInstalled}
          hasRemainingSessions={hasRemainingSessions}
          hasSelectedMembers={selectedMembers.length > 0}
          hasUpcomingBooking={hasUpcomingBooking}
          matchedFilter={matchedFilter}
          onClearAllFilters={handleClearAllFilters}
          onCompanyFilterChange={handleCompanyFilterChange}
          onMatchStatusFilterChange={handleMatchStatusFilterChange}
          onWho5FilterChange={handleWho5FilterChange}
          setHasAppInstalled={handleChatAvailableFilterChange}
          setHasRemainingSessions={handleSessionRemainingFilterChange}
          setHasUpcomingBooking={handleHasUpcomingBookingFilterChange}
          showBulkMessageModal={handleShowBulkMessageModal}
          who5Filter={who5Filter}
        />

        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress size={50} />
          </div>
        )}

        {!loading && (
          <ClientsTable
            matchedFilter={matchedFilter}
            onClearAllMembers={handleClearAllSelectedMembers}
            onReportVisit={handleReportVisit}
            onSelectAllClientsOnPage={onSelectAllClientsOnPage}
            onSelectChat={handleSelectChat}
            onSelectMember={onSelectMember}
            onSelectRow={handleSelectRow}
            orderBy={orderBy}
            searchFilter={searchFilter}
            selectAllIsChecked={hasSelectedAllClientsOnPage}
            selectedMembers={selectedMembers}
            selectedMemberUuid={currentMemberUuid}
            setOrderBy={setOrderBy}
            shouldAddColumnForMemberDrawer={memberProfileDrawerOpen}
            tableRows={filteredMembers}
          />
        )}

        {reportVisitModalOpen && currentAssignment && (
          <ReportVisitDialog
            coachAssignment={currentAssignment}
            onCancel={handleReportVisitCancel}
            onSuccess={handleReportVisitSuccess}
            showSuccessModal
          />
        )}

        {memberProfileDrawerOpen && currentMemberUuid && (
          <MemberProfileDrawer
            memberUuid={currentMemberUuid}
            navigateToIntakeFormDisplay={showCareForm.current}
            onClickExit={closeMemberProfileDrawer}
          />
        )}

        <PaginationRow
          memberCountFiltered={memberCountFiltered}
          pageNumber={pageNumber}
          pageRangeDisplayed={pageNumber === 1 || pageNumber === lastPage ? 2 : 3}
          pageSize={pageSize}
          setPageNumber={handlePaginationRowSetPageNumber}
          setPageSize={handlePaginationRowSetPageSize}
          setShowPageSizeDropdown={setShowPageSizeDropdown}
          showPageSizeDropdown={showPageSizeDropdown}
        />
      </div>

      <BulkMessageModal
        activeFilters={activeFilters}
        bulkMessageText={bulkMessageText}
        closeDialog={handleBulkMessageModalClose}
        isOpen={showBulkMessageModal}
        onPreview={handleBulkMessageModalPreview}
        selectedMembers={selectedMembers}
        setBulkMessageText={setBulkMessageText}
        setMembersToBulkMessage={setMembersToBulkMessage}
      />

      <BulkMessagePreviewModal
        bulkMessageText={bulkMessageText}
        closeDialog={handleBulkMessagePreviewModalClose}
        isOpen={showBulkMessagePreviewModal}
        membersToBulkMessage={membersToBulkMessage}
        onBackToEdit={handleBulkMessagePreviewBackToEdit}
        onSendBulkMessageSuccess={handleBulkMessagePreviewSendSuccess}
        onSendBulkMessageFailure={handleBulkMessagePreviewSendFailure}
      />

      <BulkMessageSuccessModal
        isOpen={showBulkMessageSuccessModal}
        closeDialog={handleBulkMessageSuccessClose}
      />

      <BulkMessageFailureModal
        closeDialog={handleBulkMessageFailureClose}
        isOpen={showBulkMessageFailureModal}
        membersToBulkMessage={membersToBulkMessage}
        onBackToEdit={handleBulkMessageFailureBackToEdit}
        onRetry={handleBulkMessageFailureRetry}
      />
    </PageContainer>
  );
};

export default ClientsPage;
